import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { serviceInput } from '@barbeiropro/contracts';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { MaskedInput } from '@/components/shared/masked-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { centsToInput, parseMoney } from '@/lib/format';
import { useCategories, type ServiceRow } from './api';
import { ImageUploadField } from './image-upload';
import { applyServerError, zodForm } from './lib';

interface Values {
  name: string;
  description: string;
  categoryId: string;
  durationMinutes: number;
  price: string;
  active: boolean;
  publicVisible: boolean;
  imageAssetId: string | null;
}

const NONE = 'none';
const toPayload = (v: Values) => ({
  name: v.name,
  description: v.description,
  categoryId: v.categoryId === NONE ? null : v.categoryId,
  durationMinutes: Number(v.durationMinutes),
  priceCents: parseMoney(v.price),
  active: v.active,
  publicVisible: v.publicVisible,
  imageAssetId: v.imageAssetId,
});

const empty: Values = { name: '', description: '', categoryId: NONE, durationMinutes: 30, price: centsToInput(0), active: true, publicVisible: true, imageAssetId: null };

export function ServiceFormModal({ open, onOpenChange, service }: { open: boolean; onOpenChange: (v: boolean) => void; service: ServiceRow | null }) {
  const qc = useQueryClient();
  const categories = useCategories();
  const form = useForm<Values>({ resolver: zodForm<Values>(serviceInput, toPayload, { priceCents: 'price' }), defaultValues: empty });
  const { errors } = form.formState;

  useEffect(() => {
    if (!open) return;
    form.reset(
      service
        ? {
            name: service.name,
            description: service.description ?? '',
            categoryId: service.categoryId ?? NONE,
            durationMinutes: service.durationMinutes,
            price: centsToInput(service.priceCents),
            active: service.active,
            publicVisible: service.publicVisible,
            imageAssetId: service.imageAssetId,
          }
        : empty,
    );
  }, [open, service]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = serviceInput.parse(toPayload(v));
      return service ? api.put(`/services/${service.id}`, body) : api.post('/services', body);
    },
    onSuccess: () => {
      toast.success('Serviço salvo');
      qc.invalidateQueries({ queryKey: ['catalog', 'services'] });
      qc.invalidateQueries({ queryKey: ['barbers'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e, { priceCents: 'price' }),
  });

  const duration = Number(form.watch('durationMinutes'));

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={service ? 'Editar serviço' : 'Novo serviço'}
      description="Preço e duração são o padrão; cada profissional pode ter valores próprios."
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="service-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar serviço'}
          </Button>
        </>
      }
    >
      <form id="service-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4" noValidate>
        <Field id="svc-name" label="Nome" error={errors.name?.message}>
          <Input id="svc-name" autoFocus aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <Field id="svc-desc" label="Descrição (opcional)" error={errors.description?.message} hint="Aparece no site e na página de agendamento.">
          <Textarea id="svc-desc" rows={3} maxLength={1000} {...form.register('description')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="svc-price" label="Preço" error={errors.price?.message}>
            <MaskedInput mask="money" id="svc-price" aria-invalid={!!errors.price} {...form.register('price')} />
          </Field>
          <Field id="svc-category" label="Categoria">
            <Controller
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="svc-category" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem categoria</SelectItem>
                    {categories.data?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </div>
        <Field id="svc-duration" label="Duração (minutos)" error={errors.durationMinutes?.message}>
          <div className="flex flex-wrap items-center gap-2">
            <Input id="svc-duration" type="number" min={5} max={480} step={5} className="tabular w-28" {...form.register('durationMinutes', { valueAsNumber: true })} />
            {[15, 30, 45, 60].map((m) => (
              <Button key={m} type="button" size="sm" variant={duration === m ? 'default' : 'outline'} onClick={() => form.setValue('durationMinutes', m, { shouldDirty: true, shouldValidate: true })}>
                {m} min
              </Button>
            ))}
          </div>
        </Field>
        <Controller
          control={form.control}
          name="imageAssetId"
          render={({ field }) => (
            <ImageUploadField value={field.value} onChange={field.onChange} purpose="service" label="Imagem (opcional)" defaultAlt={form.getValues('name')} shape="wide" />
          )}
        />
        <div className="grid gap-3 rounded-md border p-3">
          <Controller
            control={form.control}
            name="publicVisible"
            render={({ field }) => (
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium">Visível no site</span>
                  <span className="block text-xs text-muted-foreground">Clientes podem ver e agendar este serviço online.</span>
                </span>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </label>
            )}
          />
          <Controller
            control={form.control}
            name="active"
            render={({ field }) => (
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium">Ativo</span>
                  <span className="block text-xs text-muted-foreground">Inativo some da agenda e do site, mas mantém o histórico.</span>
                </span>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </label>
            )}
          />
        </div>
      </form>
    </FormModal>
  );
}
